/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import {
  type EstimateNewOrderRequestBody,
  DeliveryType,
  type IntercityPackageDetail,
} from '@ahm/api-wrappers-core';
// import { cleanPathObject } from '@ahm/react-map';
import { useLocationStore } from '@ahm/react-map/store';
import { useCallback, useMemo } from 'react';
import { useDetectBanHour, usePaymentMethodHook } from '@/hooks';
import { cleanPaths } from '@/lib/path-utils';
import { findInsuranceInRequests, getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';
import { InsurancePackageType } from '@/types/booking';

export function useBookingRequestBodies(
  pathname: string,
  paramPromo: string[] | null
) {
  const paths = useLocationStore.use.paths();
  const pathSortIndexes = useLocationStore.use.pathSortIndexes();

  const {
    serviceRequests: requests,
    estimatedData,
    insuranceValue,
    selectedService,
    packageDetail,
    images,
    remarks,
    serviceTypes,
    isEstimateAllService,
    orderTime,
    bikePackageSize,
    bikePackageWeight,
    itemDescriptions,
    createOrderError,
    pathInfo,
    packageImages,
    isSuggestPromotion,
    // truckSelectedGroup,
  } = useBookingStore((state) => state);
  const { paymentPayloadData } = usePaymentMethodHook();
  const { banHourData } = useDetectBanHour();
  const { isNationwide, isBikeService, isTruckService } = useMemo(
    () => getServiceType(pathname),
    [pathname]
  );

  const createRequestList = useCallback(
    (
      requests: Record<string, any>,
      serviceId?: string
    ): { _id: string; num?: number; path_index?: any }[] => {
      // special case for Insurance
      const insuranceRequest = findInsuranceInRequests(selectedService);
      if (insuranceRequest && insuranceValue !== InsurancePackageType.None) {
        requests = {
          ...requests,
          [insuranceRequest._id]: true,
        };
      }

      if (!requests || !serviceId || Object.keys(requests).length === 0)
        return [];

      return Object.entries(requests)
        .filter(([key]) => key.includes(serviceId))
        .flatMap(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((item) => ({
              _id: key,
              ...(key.includes('D2D') ? { path_index: item } : item),
            }));
          }

          if (value && typeof value === 'object') {
            return [{ _id: key, ...value }];
          }

          if (value) {
            return [
              {
                _id: key,
                ...(typeof value === 'number' ? { num: value } : {}),
              },
            ];
          }

          return [];
        });
    },
    [insuranceValue, serviceTypes, selectedService?._id]
  );

  const packageDetailPerService = useMemo(() => {
    if (isNationwide) {
      return { package_detail: packageDetail, package_images: packageImages };
    }
    if (isBikeService) {
      return {
        package_detail: [],
        ...(bikePackageSize && { package_size: bikePackageSize }),
        ...(bikePackageWeight && { package_weight: bikePackageWeight }),
        ...(itemDescriptions && {
          item_descriptions: itemDescriptions,
        }),
      };
    }
    if (isTruckService) {
      return {
        package_dimension_unit: 'cm',
        package_detail: packageDetail.map((item) => ({
          ...item,
          image_url: item.image_url?.[0],
          count: 1,
          quantity: 1,
        })),
        item_descriptions: itemDescriptions,
      };
    }
  }, [
    isTruckService,
    isNationwide,
    isBikeService,
    packageDetail,
    bikePackageSize,
    bikePackageWeight,
    itemDescriptions,
  ]);

  const feeViaService = useMemo(() => {
    if (!estimatedData) return;
    if (isNationwide) {
      return {
        total_price: estimatedData?.data?.total_price,
      };
    }
    if (isBikeService || isTruckService) {
      return {
        total_pay: estimatedData?.data?.total_pay,
      };
    }
  }, [isNationwide, isBikeService, estimatedData]);

  const createServicesParams = useCallback(() => {
    if (!selectedService?._id && !isEstimateAllService) {
      return [];
    }

    if (isEstimateAllService && serviceTypes?.length) {
      return serviceTypes.map((service) => {
        const requestList = createRequestList(requests ?? {}, service._id);
        return {
          _id: service._id,
          requests:
            service.requests?.reduce<
              { _id: string; path_index?: any; num?: number }[]
            >((acc, req) => {
              const matchingItem = requestList.find(
                (item) => item._id === req._id
              );
              if (matchingItem) {
                acc.push(matchingItem);
              }
              return acc;
            }, []) || [],
        };
      });
    }

    return [
      {
        _id: selectedService!._id,
        requests: createRequestList(requests ?? {}, selectedService!._id),
      },
    ];
  }, [selectedService, isEstimateAllService, requests, createRequestList]);

  const nationwidePathInfo = useMemo(() => {
    return pathInfo?.[1];
  }, [pathInfo]);

  const updateNationwidePath = (currentPaths: any[]) => {
    if (
      !nationwidePathInfo ||
      selectedService?.delivery_type !== DeliveryType.Nationwide ||
      !currentPaths[1]
    ) {
      return currentPaths;
    }

    const updatedPaths = [...currentPaths];
    updatedPaths[1] = {
      ...updatedPaths[1],
      ...nationwidePathInfo,
    };
    return updatedPaths;
  };

  const updatePath = (currentPaths: any[]) => {
    if (selectedService?.delivery_type !== 'TRUCK-HM') {
      return currentPaths.map((p) => {
        const { location_features, ...rest } = p;
        return rest;
      });
    }
    return currentPaths;
  };

  const isValidRequest = () => {
    // Common validation checks
    if (paths.length < 2 || !selectedService?._id || createOrderError) {
      return false;
    }

    const { delivery_type } = selectedService;
    // Nationwide delivery validation
    if (delivery_type === DeliveryType.Nationwide) {
      return (
        !!paymentPayloadData?.payment_method_id &&
        (packageDetail as IntercityPackageDetail[])?.length > 0
      );
    }

    // Truck service validation
    if (isTruckService && banHourData) {
      return false;
    }

    // Default validation for other services
    return !!paymentPayloadData?.payment_method;
  };

  const estimateRequestBody = useMemo(() => {
    if (!isValidRequest()) return undefined;
    const isNationwideDelivery =
      selectedService?.delivery_type === DeliveryType.Nationwide;

    const servicesParams = createServicesParams();
    const updatedPaths = updatePath(
      isNationwideDelivery
        ? updateNationwidePath(paths)
        : pathSortIndexes
          ? pathSortIndexes.map((idx) => paths[idx])
          : paths
    );

    return {
      serviceId: selectedService?._id,
      path: updatedPaths,
      services: servicesParams,
      ...(paramPromo?.length
        ? {
            promo_code_ids: paramPromo,
          }
        : { suggest_promotion: isSuggestPromotion }),
      order_time: orderTime,
      idle_until: orderTime && orderTime > 0 ? orderTime : undefined,
      images,
      remarks,
      ...paymentPayloadData,
      ...packageDetailPerService,
      // ...(isTruckService && { package_dimension_unit: 'cm' }),
    } as unknown as EstimateNewOrderRequestBody;
  }, [
    selectedService,
    paths,
    pathSortIndexes,
    paramPromo,
    packageDetail,
    nationwidePathInfo,
    orderTime,
    images,
    remarks,
    paymentPayloadData,
    packageDetailPerService,
    isTruckService,
    createOrderError,
    createServicesParams,
  ]);

  const createRequestBody = useMemo(() => {
    if (
      !selectedService ||
      !estimateRequestBody ||
      (estimatedData && estimatedData.data?.total_price === undefined)
    )
      return undefined;

    const { path, promo_code_ids, services, order_time, idle_until } =
      estimateRequestBody;

    const serviceForCreate = services.find(
      (s) => s._id === selectedService?._id
    );

    const cleanedPaths = cleanPaths(path);

    return {
      path: cleanedPaths,
      requests: serviceForCreate?.requests ?? [],
      service_id: selectedService._id,
      service: serviceForCreate,
      remarks,
      promo_code_ids,
      images,
      order_time,
      idle_until,
      app_source: 'web_user', // Assuming this from a web user
      ...feeViaService,
      ...paymentPayloadData,
      ...packageDetailPerService,
    };
  }, [
    estimateRequestBody,
    estimatedData,
    paymentPayloadData,
    remarks,
    images,
    feeViaService,
  ]);

  return { estimateRequestBody, createRequestBody, createRequestList };
}
